import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useToast } from '../contexts/ToastContext';
import LoadingSpinner from './LoadingSpinner';
import Step1ParrotConfig from './Step1ParrotConfig';
import Step2VoiceConfig from './Step2VoiceConfig';
import Step3AIConfig from './Step3AIConfig';
import { ApiClient } from '../utils/apiClient';

/**
 * Parrot Wizard Edit Component
 * Provides a 3-step wizard interface for editing existing parrots with pre-populated data
 */
const ParrotWizardEdit = ({ parrotId, onComplete, onCancel }) => {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { showToast } = useToast();
  const apiClient = useRef(new ApiClient(getToken)).current;

  // State management
  const [currentStep, setCurrentStep] = useState(1);
  const [sessionId, setSessionId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  
  // Form data for each step
  const [step1Data, setStep1Data] = useState({});
  const [step2Data, setStep2Data] = useState({});
  const [step3Data, setStep3Data] = useState({});
  
  // Original parrot data
  const [originalParrot, setOriginalParrot] = useState(null);

  // Load existing parrot data on mount
  useEffect(() => {
    if (parrotId) {
      loadParrotData();
    }
  }, [parrotId]);

  /**
   * Load existing parrot data and populate wizard steps
   */
  const loadParrotData = async () => {
    try {
      setIsLoading(true);
      setErrors({});

      // Fetch parrot data
      const parrotData = await apiClient.get(`/api/parrots/${parrotId}`);
      setOriginalParrot(parrotData);

      // Parse the configuration to extract wizard-compatible data
      const config = parrotData.configuration ? JSON.parse(parrotData.configuration) : {};
      
      // Map parrot data to Step 1 format
      const mappedStep1Data = {
        parrot_id: parrotId, // Add parrot ID for validation
        name: parrotData.name || '',
        language: config.conversation_config?.agent?.language || 'en', // Ensure language defaults to 'en'
        max_call_length_seconds: config.conversation_config?.max_duration_seconds || '',
        turn_timeout_seconds: Math.round((config.conversation_config?.turn_detection?.silence_duration_ms || 7000) / 1000),
        max_silence_seconds: config.conversation_config?.silence_timeout_ms === -1 ? '' : 
          Math.round((config.conversation_config?.silence_timeout_ms || 20000) / 1000),
        first_message: config.conversation_config?.agent?.first_message || '',
        selected_template_id: parrotData.quick_action_template_id || null,
        existing_system_prompt: config.conversation_config?.agent?.prompt?.prompt || null
      };

      // Map parrot data to Step 2 format
      const mappedStep2Data = {
        voice_id: config.voice?.voice_id || '',
        creativity_level: mapTemperatureToCreativity(config.conversation_config?.agent?.prompt?.temperature || 0.7),
        stability: mapNumericToButton(config.voice?.stability || 0.5, 'stability'),
        similarity_boost: mapNumericToButton(config.voice?.similarity_boost || 0.8, 'similarity_boost'),
        style: mapNumericToButton(config.voice?.style || 0.0, 'style')
      };

      console.log('Setting step1Data with parrot_id:', parrotId, 'mappedStep1Data:', mappedStep1Data);
      setStep1Data(mappedStep1Data);
      setStep2Data(mappedStep2Data);

      // Load selected template if available
      if (parrotData.quick_action_template_id) {
        try {
          const templates = await apiClient.get('/api/quick-action-templates/active');
          const template = templates.find(t => t.id === parrotData.quick_action_template_id);
          if (template) {
            setSelectedTemplate(template);
          }
        } catch (error) {
          console.warn('Failed to load template:', error);
        }
      }

    } catch (error) {
      console.error('Failed to load parrot data:', error);
      setErrors({ general: 'Failed to load parrot data. Please try again.' });
      showToast('Failed to load parrot data', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Map temperature value to creativity level
   */
  const mapTemperatureToCreativity = (temperature) => {
    if (temperature <= 0.3) return 'deterministic';
    if (temperature <= 0.9) return 'creative';
    return 'more_creative';
  };

  /**
   * Map numeric value back to button value
   */
  const mapNumericToButton = (numericValue, setting) => {
    const mappings = {
      stability: { 0.2: 'low', 0.5: 'medium', 0.8: 'high' },
      similarity_boost: { 0.3: 'low', 0.6: 'medium', 0.9: 'high' },
      style: { 0.1: 'low', 0.4: 'medium', 0.7: 'high' }
    };

    const settingMap = mappings[setting];
    if (!settingMap) return 'medium';

    // Find closest match
    let closestValue = 'medium';
    let closestDiff = Infinity;

    Object.entries(settingMap).forEach(([numeric, button]) => {
      const diff = Math.abs(parseFloat(numeric) - numericValue);
      if (diff < closestDiff) {
        closestDiff = diff;
        closestValue = button;
      }
    });

    return closestValue;
  };

  /**
   * Handle Step 1 form data changes
   */
  const handleStep1Change = (data) => {
    setStep1Data(prev => ({ ...prev, ...data }));
    setErrors(prev => ({ ...prev, step1: null }));
  };

  /**
   * Handle Step 1 completion and move to Step 2
   */
  const handleStep1Next = async (data) => {
    try {
      setIsSaving(true);
      setErrors({});

      // Call backend Step 1 API with parrot_id for editing
      const response = await apiClient.post('/api/parrots/wizard/step1', {
        session_id: sessionId,
        parrot_id: parrotId,
        ...data
      });

      if (response.success) {
        setSessionId(response.session_id);
        setStep1Data(data);
        setCurrentStep(2);
        showToast('Step 1 completed successfully', 'success');
      } else {
        throw new Error(response.error || 'Failed to complete Step 1');
      }

    } catch (error) {
      console.error('Step 1 error:', error);
      setErrors({ step1: error.message || 'Failed to complete Step 1' });
      showToast('Failed to complete Step 1', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Handle Step 2 form data changes
   */
  const handleStep2Change = (data) => {
    setStep2Data(prev => ({ ...prev, ...data }));
    setErrors(prev => ({ ...prev, step2: null }));
  };

  /**
   * Handle Step 2 completion and move to Step 3
   */
  const handleStep2Next = async (data) => {
    try {
      setIsSaving(true);
      setErrors({});

      // Call backend Step 2 API
      const response = await apiClient.post('/api/parrots/wizard/step2', {
        session_id: sessionId,
        ...data
      });

      if (response.success) {
        setStep2Data(data);
        setCurrentStep(3);
        showToast('Step 2 completed successfully', 'success');
      } else {
        throw new Error(response.error || 'Failed to complete Step 2');
      }

    } catch (error) {
      console.error('Step 2 error:', error);
      setErrors({ step2: error.message || 'Failed to complete Step 2' });
      showToast('Failed to complete Step 2', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Handle Step 2 save and exit
   */
  const handleStep2SaveAndExit = async (data) => {
    try {
      setIsSaving(true);
      setErrors({});

      // Call backend Step 2 API
      const response = await apiClient.post('/api/parrots/wizard/step2', {
        session_id: sessionId,
        ...data
      });

      if (response.success) {
        // Finalize without Step 3
        await finalizeParrot();
      } else {
        throw new Error(response.error || 'Failed to save configuration');
      }

    } catch (error) {
      console.error('Step 2 save error:', error);
      setErrors({ step2: error.message || 'Failed to save configuration' });
      showToast('Failed to save configuration', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Handle Step 3 completion
   */
  const handleStep3Complete = async (data) => {
    try {
      setIsSaving(true);
      setErrors({});

      setStep3Data(data);
      await finalizeParrot();

    } catch (error) {
      console.error('Step 3 error:', error);
      setErrors({ step3: error.message || 'Failed to complete configuration' });
      showToast('Failed to complete configuration', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Finalize parrot configuration
   */
  const finalizeParrot = async () => {
    try {
      // Prepare finalize data with session ID and any Step 3 data
      const finalizeData = {
        session_id: sessionId
      };
      
      // Include Step 3 data if available (system_prompt and first_message)
      if (step3Data && (step3Data.system_prompt || step3Data.first_message)) {
        if (step3Data.system_prompt) {
          finalizeData.system_prompt = step3Data.system_prompt;
        }
        if (step3Data.first_message) {
          finalizeData.first_message = step3Data.first_message;
        }
      }
      
      const response = await apiClient.post('/api/parrots/wizard/finalize', finalizeData);

      if (response.success) {
        showToast('Parrot updated successfully!', 'success');
        
        if (onComplete) {
          onComplete(response);
        } else {
          // Navigate back to parrots list after a short delay
          setTimeout(() => {
            navigate('/parrots');
          }, 1500);
        }
      } else {
        throw new Error(response.error || 'Failed to finalize parrot');
      }

    } catch (error) {
      console.error('Finalize error:', error);
      throw error;
    }
  };

  /**
   * Handle back navigation between steps
   */
  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  /**
   * Handle cancel and return to parrot list
   */
  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      navigate('/parrots');
    }
  };

  // Show loading spinner while loading parrot data
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
        <span className={`ml-3 text-lg ${
          theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
        }`}>
          Loading parrot configuration...
        </span>
      </div>
    );
  }

  // Show error if failed to load
  if (errors.general) {
    return (
      <div className={`text-center py-12 ${
        theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
      }`}>
        <div className={`mb-4 p-4 rounded-lg ${
          theme === 'dark' ? 'bg-red-900/20 text-red-300' : 'bg-red-50 text-red-700'
        }`}>
          {errors.general}
        </div>
        <button
          onClick={() => loadParrotData()}
          className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg transition-colors dark:text-white"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="parrot-wizard-edit">
      {/* Progress Indicator */}
      <div className="mb-8">
        <div className="flex items-center justify-center space-x-8">
          {[1, 2, 3].map((step) => (
            <div key={step} className="flex items-center">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                step < currentStep
                  ? 'bg-teal-600 text-white'
                  : step === currentStep
                    ? 'bg-teal-600 text-white'
                    : theme === 'dark'
                      ? 'bg-gray-700 text-gray-400'
                      : 'bg-gray-200 text-gray-500'
              }`}>
                {step < currentStep ? (
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                ) : (
                  step
                )}
              </div>
              <div className={`ml-3 ${
                step <= currentStep
                  ? theme === 'dark' ? 'text-white' : 'text-gray-900'
                  : theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              }`}>
                <div className="font-medium">
                  {step === 1 && 'Basic Configuration'}
                  {step === 2 && 'Voice Settings'}
                  {step === 3 && 'AI Refinement'}
                </div>
                <div className="text-sm">
                  {step === 1 && 'Name, timing, and templates'}
                  {step === 2 && 'Voice and behavior settings'}
                  {step === 3 && 'AI chat customization'}
                </div>
              </div>
              {step < 3 && (
                <div className={`ml-8 w-16 h-0.5 ${
                  step < currentStep
                    ? 'bg-teal-600'
                    : theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                }`} />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Step Content */}
      <div className="wizard-content">
        {currentStep === 1 && (
          <Step1ParrotConfig
            formData={step1Data}
            onFormDataChange={handleStep1Change}
            onNext={handleStep1Next}
            onCancel={handleCancel}
            errors={errors.step1 ? { general: errors.step1 } : {}}
            isLoading={isSaving}
          />
        )}

        {currentStep === 2 && (
          <Step2VoiceConfig
            formData={step2Data}
            onFormDataChange={handleStep2Change}
            onNext={handleStep2Next}
            onSaveAndExit={handleStep2SaveAndExit}
            onBack={handleBack}
            errors={errors.step2 ? { general: errors.step2 } : {}}
            isLoading={isSaving}
          />
        )}

        {currentStep === 3 && (
          <Step3AIConfig
            formData={{ 
              ...step1Data, 
              ...step2Data, 
              session_id: sessionId,
              existing_system_prompt: step1Data.existing_system_prompt
            }}
            selectedTemplate={selectedTemplate}
            onComplete={handleStep3Complete}
            onBack={handleBack}
            onSaveAndExit={handleStep2SaveAndExit}
            errors={errors.step3 ? { general: errors.step3 } : {}}
            isLoading={isSaving}
            isEditing={true}
            existingParrotId={parrotId}
          />
        )}
      </div>

      {/* Debug Info (only in development) */}
      {process.env.NODE_ENV === 'development' && (
        <div className={`mt-8 p-4 rounded-lg text-xs ${
          theme === 'dark' ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-600'
        }`}>
          <details>
            <summary className="cursor-pointer font-medium">Debug Info</summary>
            <div className="mt-2 space-y-2">
              <div>Current Step: {currentStep}</div>
              <div>Session ID: {sessionId}</div>
              <div>Parrot ID: {parrotId}</div>
              <div>Selected Template: {selectedTemplate?.name || 'None'}</div>
              <div>Step 1 Data: {JSON.stringify(step1Data, null, 2)}</div>
              <div>Step 2 Data: {JSON.stringify(step2Data, null, 2)}</div>
            </div>
          </details>
        </div>
      )}
    </div>
  );
};

export default ParrotWizardEdit;
